import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useGetLedgerAccountBalance, useRecordDiscount } from '../../../services/loanService';
import { TxnBucket } from '../../../services/schemas/loanServiceSchemas/txnBucket';
import { formatCurrency } from '../../../utils/formatters';
import { toLocalDateString } from '../../../utils/localDate';

interface Props {
  accountId?: string;
  show: boolean;
  onHide: () => void;
  /** Called after the discount is posted, so the page can reload the balance and ledger. */
  onRecorded?: () => void;
}

const BUCKETS: { value: string; label: string; balanceKey: 'penaltiesBalance' | 'feesBalance' | 'interestBalance' | 'principalBalance' }[] = [
  { value: 'PENALTIES', label: 'Penalties', balanceKey: 'penaltiesBalance' },
  { value: 'FEES', label: 'Fees', balanceKey: 'feesBalance' },
  { value: 'INTEREST', label: 'Interest', balanceKey: 'interestBalance' },
  { value: 'PRINCIPAL', label: 'Principal', balanceKey: 'principalBalance' },
];

/**
 * A discount or waiver agreed with the client - a negotiated settlement, penalties waived, goodwill.
 * It needs a reason and who approved it; both are kept on the ledger entry. It can take off no more
 * than is outstanding on the part of the balance it is applied to.
 */
const DiscountWaiverModal: React.FC<Props> = ({ accountId, show, onHide, onRecorded }) => {
  const { data: balance, refetch } = useGetLedgerAccountBalance(accountId || '', undefined, {
    query: { enabled: show && !!accountId }
  });
  const recordDiscount = useRecordDiscount();

  const [bucket, setBucket] = useState('PENALTIES');
  const [amount, setAmount] = useState('');
  const [valueDate, setValueDate] = useState(toLocalDateString());
  const [reason, setReason] = useState('');
  const [approvedBy, setApprovedBy] = useState('');

  useEffect(() => {
    if (show) {
      setAmount('');
      setReason('');
      setApprovedBy('');
      setValueDate(toLocalDateString());
      refetch();
    }
  }, [show, refetch]);

  const outstanding = useMemo(() => {
    const key = BUCKETS.find(b => b.value === bucket)?.balanceKey;
    return key && balance ? Math.max(Number(balance[key] || 0), 0) : 0;
  }, [balance, bucket]);

  const amountValue = Number(amount);
  const amountError = amount === ''
    ? ''
    : !(amountValue > 0)
      ? 'Enter an amount greater than zero'
      : amountValue > outstanding
        ? `No more than the ${formatCurrency(outstanding)} outstanding on ${bucket.toLowerCase()}`
        : '';
  const canSubmit = !!accountId && amount !== '' && !amountError && reason.trim() !== '' && approvedBy.trim() !== ''
    && !recordDiscount.isPending;

  const submit = async () => {
    if (!accountId || !canSubmit) return;
    try {
      await recordDiscount.mutateAsync({
        data: {
          accountId,
          bucket: bucket as TxnBucket,
          amount: amountValue,
          valueDate,
          idempotencyKey: `discount-${accountId}-${Date.now()}`,
          remarks: reason.trim(),
          approvedBy: approvedBy.trim(),
        }
      });
      toast.success(`Discount of ${formatCurrency(amountValue)} on ${bucket.toLowerCase()} recorded`);
      onRecorded?.();
      onHide();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to record the discount');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Discount / waiver</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Reduces what the client owes - for example a negotiated settlement or penalties waived. It is
          posted to the ledger with the reason and approver, and the balance and installments update.
        </p>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Apply to</Form.Label>
            <Form.Select value={bucket} onChange={e => setBucket(e.target.value)}>
              {BUCKETS.map(b => (
                <option key={b.value} value={b.value}>
                  {b.label}{balance ? ` — ${formatCurrency(Math.max(Number(balance[b.balanceKey] || 0), 0))} outstanding` : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <Form.Label>Amount (R)</Form.Label>
              <Form.Control type="number" min={0} step="0.01" value={amount}
                onChange={e => setAmount(e.target.value)} isInvalid={!!amountError} placeholder="0.00" />
              <Form.Control.Feedback type="invalid">{amountError}</Form.Control.Feedback>
              {outstanding > 0 && (
                <Button variant="link" size="sm" className="p-0 mt-1" onClick={() => setAmount(outstanding.toFixed(2))}>
                  Waive all {formatCurrency(outstanding)}
                </Button>
              )}
            </div>
            <div className="col-md-6">
              <Form.Label>Value date</Form.Label>
              <Form.Control type="date" value={valueDate} onChange={e => setValueDate(e.target.value)} />
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Reason <span className="text-danger">*</span></Form.Label>
            <Form.Control as="textarea" rows={2} value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Negotiated settlement agreed 05/10/2026" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Approved by <span className="text-danger">*</span></Form.Label>
            <Form.Control type="text" maxLength={100} value={approvedBy} onChange={e => setApprovedBy(e.target.value)}
              placeholder="Name of the person who approved it" />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={!canSubmit}>
          {recordDiscount.isPending ? 'Recording...' : 'Record discount'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DiscountWaiverModal;
